import { mathService } from '@approvalmax/utils';
import uniq from 'lodash/uniq';
import uniqueId from 'lodash/uniqueId';
import { backend, domain } from 'modules/data';
import moment from 'moment';
import { calcTaxesOnCombinedRates, getTaxCodeEffectiveRates } from 'pages/requestForm/selectors/qbooks/taxSelectors';

import { messages } from './QBooksJournalEntryRequestForm.messages';
import {
    ExpandedJournalEntryLine,
    JournalEntryFields,
    QBooksJournalEntryLineWithTaxCode,
} from './QBooksJournalEntryRequestForm.types';

export const createNewJournalEntryLine = (): ExpandedJournalEntryLine => {
    return {
        id: uniqueId('qbooksJournalEntryLine-'),
        account: null,
        amount: null,
        amountType: null,
        description: null,
        payeeType: null,
        payee: null,
        taxCode: null,
        taxRateId: null,
        taxApplicableOnType: null,
        class: null,
        department: null,
        isEmpty: true,
    };
};

export const expandJournalEntryLine = (line: QBooksJournalEntryLineWithTaxCode): ExpandedJournalEntryLine => {
    return {
        ...line,
        id: line.id || uniqueId('qbooksJournalEntryLine-'),
        isEmpty: checkJournalEntryLineIsEmpty(line),
    };
};

export const checkJournalEntryLineIsEmpty = (line: QBooksJournalEntryLineWithTaxCode) => {
    return (
        !line.account &&
        typeof line.amount !== 'number' &&
        !line.description &&
        !line.payeeType &&
        !line.payee &&
        !line.taxCode &&
        !line.class &&
        !line.department
    );
};

export const checkJournalEntryLinesAreEmpty = (lines: QBooksJournalEntryLineWithTaxCode[]) => {
    return lines.every((line) => checkJournalEntryLineIsEmpty(line));
};

export const checkJournalEntryLinesAreInvalid = (
    lines: QBooksJournalEntryLineWithTaxCode[],
    isClassFieldRequired: boolean,
    isDepartmentFieldRequired: boolean,
    accounts: domain.QBooksAccount[]
): boolean => {
    const hasInvalidLine = lines.find((line) => {
        if (checkJournalEntryLineIsEmpty(line)) {
            return false;
        }

        if (!line.account || typeof line.amount !== 'number') {
            return true;
        }

        if (isClassFieldRequired && !line.class) {
            return true;
        }

        if (isDepartmentFieldRequired && !line.department) {
            return true;
        }

        if (line.account) {
            const accountInfo = accounts.find((account) => account.id === line.account?.id);

            if (accountInfo?.type === domain.QBooksAccountType.AccountsPayable) {
                return line.payeeType?.id !== backend.IntegrationsQBooksPayeeTypes.Vendor;
            }

            if (accountInfo?.type === domain.QBooksAccountType.AccountsReceivable) {
                return line.payeeType?.id !== backend.IntegrationsQBooksPayeeTypes.Customer;
            }
        }

        return false;
    });

    return Boolean(hasInvalidLine);
};

export const getValidationErrors = (
    fields: JournalEntryFields,
    lines: QBooksJournalEntryLineWithTaxCode[],
    isClassFieldRequired: boolean,
    isDepartmentFieldRequired: boolean,
    accounts: domain.QBooksAccount[]
): string[] => {
    const errors: string[] = [];

    const areLinesInvalid = checkJournalEntryLinesAreInvalid(
        lines,
        isClassFieldRequired,
        isDepartmentFieldRequired,
        accounts
    );

    if (!fields.currency || !fields.journalDate || !fields.journalNumber || areLinesInvalid) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (fields.journalNumber && fields.journalNumber.length > 21) {
        errors.push(messages.journalNumberLengthError);
    }

    let hasAccountPayablePayeeTypeMismatch = false;
    let hasAccountReceivablePayeeTypeMismatch = false;
    let hasUnfilledPayee = false;

    lines.forEach((line) => {
        if (line.account) {
            const accountInfo = accounts.find((account) => account.id === line.account?.id);

            if (
                accountInfo?.type === domain.QBooksAccountType.AccountsPayable &&
                line.payeeType?.id !== backend.IntegrationsQBooksPayeeTypes.Vendor
            ) {
                hasAccountPayablePayeeTypeMismatch = true;
            }

            if (
                accountInfo?.type === domain.QBooksAccountType.AccountsReceivable &&
                line.payeeType?.id !== backend.IntegrationsQBooksPayeeTypes.Customer
            ) {
                hasAccountReceivablePayeeTypeMismatch = true;
            }
        }

        if (line.payeeType && !line.payee) {
            hasUnfilledPayee = true;
        }
    });

    if (hasAccountPayablePayeeTypeMismatch) {
        errors.push(messages.accountPayablePayeeTypeMismatchError);
    }

    if (hasAccountReceivablePayeeTypeMismatch) {
        errors.push(messages.accountReceivablePayeeTypeMismatchError);
    }

    if (hasUnfilledPayee) {
        errors.push(messages.mandatoryFieldsError);
    }

    return uniq(errors);
};

export const getTaxName = (
    taxCodeName: string,
    taxPercent: number,
    taxApplicableOnType: keyof typeof domain.TaxApplicableOnType | null
) => {
    const taxApplicableOnTypeText =
        taxApplicableOnType === domain.TaxApplicableOnType.Sales ? messages.sales : messages.purchases;

    return `${taxCodeName} (${taxPercent}% ${taxApplicableOnTypeText})`;
};

export const calculateAmounts = (
    lines: QBooksJournalEntryLineWithTaxCode[],
    lineAmountType: domain.LineAmountType,
    date: string,
    qbooksContext: domain.QBooksContext
) => {
    const result: {
        debitSubTotal: number;
        creditSubTotal: number;
        debitTotal: number;
        creditTotal: number;
        taxes: Record<
            string,
            {
                taxName: string;
                debit: number;
                credit: number;
                taxableDebitAmount: number;
                taxableCreditAmount: number;
                taxCodeId: string;
                taxRateId: string;
                taxApplicableOnType: keyof typeof domain.TaxApplicableOnType | null;
                taxPercent: number;
            }
        >;
        debitTotalTax: number;
        creditTotalTax: number;
    } = {
        debitSubTotal: 0,
        creditSubTotal: 0,
        debitTotal: 0,
        creditTotal: 0,
        taxes: {},
        debitTotalTax: 0,
        creditTotalTax: 0,
    };

    lines.forEach((line) => {
        const lineAmount = mathService.round(line.amount || 0, 2);

        if (lineAmountType === domain.LineAmountType.NoTax || !line.taxCode) {
            if (line.amountType === domain.QBooksJournalEntryLineAmountType.Debit) {
                result.debitSubTotal = mathService.add(result.debitSubTotal, lineAmount || 0);
                result.debitTotal = mathService.add(result.debitTotal, lineAmount || 0);
            }

            if (line.amountType === domain.QBooksJournalEntryLineAmountType.Credit) {
                result.creditSubTotal = mathService.add(result.creditSubTotal, lineAmount || 0);
                result.creditTotal = mathService.add(result.creditTotal, lineAmount || 0);
            }

            return;
        }

        if (!line.taxCode) {
            return;
        }

        if (line.taxCode.taxRateId) {
            const taxPercent = line.taxCode.rate ?? 0;

            const taxableAmount =
                (lineAmount * 100) / (100 + (lineAmountType === domain.LineAmountType.TaxExclusive ? 0 : taxPercent));

            const taxAmount = Math.round(taxableAmount * taxPercent) / 100;

            const taxKey = `${line.taxCode.id}-${line.taxCode.taxRateId}`;

            if (!result.taxes[taxKey]) {
                result.taxes[taxKey] = {
                    debit: 0,
                    credit: 0,
                    taxableDebitAmount: 0,
                    taxableCreditAmount: 0,
                    taxName: getTaxName(line.taxCode.taxCodeName, taxPercent, line.taxCode.taxApplicableOnType),
                    taxCodeId: line.taxCode.taxCodeId,
                    taxRateId: line.taxCode.taxRateId,
                    taxApplicableOnType: line.taxCode.taxApplicableOnType,
                    taxPercent,
                };
            }

            if (line.amountType === domain.QBooksJournalEntryLineAmountType.Debit) {
                result.taxes[taxKey].debit = mathService.round(
                    mathService.add(result.taxes[taxKey].debit, taxAmount),
                    2
                );

                result.taxes[taxKey].taxableDebitAmount = mathService.round(
                    mathService.add(result.taxes[taxKey].taxableDebitAmount, lineAmount),
                    2
                );

                result.debitSubTotal = mathService.add(result.debitSubTotal, lineAmount);

                result.debitTotal = mathService.add(result.debitTotal, lineAmount);

                if (lineAmountType === domain.LineAmountType.TaxExclusive) {
                    result.debitTotal = mathService.add(result.debitTotal, taxAmount);
                }
            }

            if (line.amountType === domain.QBooksJournalEntryLineAmountType.Credit) {
                result.taxes[taxKey].credit = mathService.round(
                    mathService.add(result.taxes[taxKey].credit, taxAmount),
                    2
                );

                result.taxes[taxKey].taxableCreditAmount = mathService.round(
                    mathService.add(result.taxes[taxKey].taxableCreditAmount, lineAmount),
                    2
                );

                result.creditSubTotal = mathService.add(result.creditSubTotal, lineAmount);

                result.creditTotal = mathService.add(result.creditTotal, lineAmount);

                if (lineAmountType === domain.LineAmountType.TaxExclusive) {
                    result.creditTotal = mathService.add(result.creditTotal, taxAmount);
                }
            }
        }

        if (line.taxCode.isGroupTax && line.taxCode.taxRates?.length) {
            const expandedTaxCode = getTaxCodeEffectiveRates(
                line.taxCode.taxCodeId,
                date,
                qbooksContext,
                line.taxCode.taxApplicableOnType
            );

            const taxableAmount =
                ((line.amount ?? 0) * 100) /
                (100 + (lineAmountType === domain.LineAmountType.TaxExclusive ? 0 : expandedTaxCode.effectiveRate));

            const taxes = calcTaxesOnCombinedRates(taxableAmount, expandedTaxCode.combinedRates);

            if (line.amountType === domain.QBooksJournalEntryLineAmountType.Debit) {
                result.debitSubTotal = mathService.add(result.debitSubTotal, lineAmount);

                result.debitTotal = mathService.add(result.debitTotal, lineAmount);
            }

            if (line.amountType === domain.QBooksJournalEntryLineAmountType.Credit) {
                result.creditSubTotal = mathService.add(result.creditSubTotal, lineAmount);

                result.creditTotal = mathService.add(result.creditTotal, lineAmount);
            }

            expandedTaxCode.combinedRates.forEach((combinedRate, index) => {
                const taxKey = `${line.taxCode?.id}-${combinedRate.taxRateId}`;

                if (!result.taxes[taxKey]) {
                    result.taxes[taxKey] = {
                        debit: 0,
                        credit: 0,
                        taxableDebitAmount: 0,
                        taxableCreditAmount: 0,
                        taxName: getTaxName(
                            line.taxCode?.taxCodeName ?? '',
                            combinedRate.rate,
                            line.taxCode?.taxApplicableOnType ?? null
                        ),
                        taxCodeId: line.taxCode?.taxCodeId ?? '',
                        taxRateId: combinedRate.taxRateId,
                        taxApplicableOnType: line.taxCode?.taxApplicableOnType ?? null,
                        taxPercent: combinedRate.rate,
                    };
                }

                const taxAmount = mathService.round(taxes[index].taxAmount, 2);

                if (line.amountType === domain.QBooksJournalEntryLineAmountType.Debit) {
                    result.taxes[taxKey].debit = mathService.round(
                        mathService.add(result.taxes[taxKey].debit, taxAmount),
                        2
                    );

                    result.taxes[taxKey].taxableDebitAmount = mathService.round(
                        mathService.add(result.taxes[taxKey].taxableDebitAmount, lineAmount),
                        2
                    );

                    if (lineAmountType === domain.LineAmountType.TaxExclusive) {
                        result.debitTotal = mathService.add(result.debitTotal, taxAmount);
                    }
                }

                if (line.amountType === domain.QBooksJournalEntryLineAmountType.Credit) {
                    result.taxes[taxKey].credit = mathService.round(
                        mathService.add(result.taxes[taxKey].credit, taxAmount),
                        2
                    );

                    result.taxes[taxKey].taxableCreditAmount = mathService.round(
                        mathService.add(result.taxes[taxKey].taxableCreditAmount, lineAmount),
                        2
                    );

                    if (lineAmountType === domain.LineAmountType.TaxExclusive) {
                        result.creditTotal = mathService.add(result.creditTotal, taxAmount);
                    }
                }
            });
        }
    });

    result.debitTotalTax = Object.values(result.taxes).reduce((acc, taxValue) => {
        return mathService.add(acc, taxValue.debit);
    }, 0);

    result.creditTotalTax = Object.values(result.taxes).reduce((acc, taxValue) => {
        return mathService.add(acc, taxValue.credit);
    }, 0);

    result.debitSubTotal = mathService.round(result.debitSubTotal, 2);
    result.debitTotal = mathService.round(result.debitTotal, 2);
    result.creditSubTotal = mathService.round(result.creditSubTotal, 2);
    result.creditTotal = mathService.round(result.creditTotal, 2);
    result.debitTotalTax = mathService.round(result.debitTotalTax, 2);
    result.creditTotalTax = mathService.round(result.creditTotalTax, 2);

    return result;
};

export const findActiveEffectiveRate = (
    effectiveTaxRateList: domain.QBooksTaxRate['effectiveTaxRate'],
    date: string
) => {
    const requestDate = moment.utc(date);

    return effectiveTaxRateList.find((rateItem) => {
        const isStartDateIncluded =
            rateItem.startDate !== null ? moment.utc(rateItem.startDate).isSameOrBefore(requestDate) : true;

        const isEndDateIncluded =
            rateItem.endDate !== null ? moment.utc(rateItem.endDate).isSameOrAfter(requestDate) : true;

        return isStartDateIncluded && isEndDateIncluded;
    });
};

export const getUnavailableTaxRateIds = (taxRateList: domain.QBooksTaxRate[], date: string | null) => {
    return date
        ? taxRateList
              .filter((taxRate) => {
                  const effectiveTaxRate = findActiveEffectiveRate(taxRate.effectiveTaxRate, date);

                  return !effectiveTaxRate;
              })
              .map((taxRate) => taxRate.id)
        : [];
};
